import { SearchModel } from './common';

/**
 * Tipos para Empresas do Grupo Econômico conforme schema [Financial].[Company]
 *
 * Domínio compartilhado — utilizado por múltiplas entidades do sistema
 * (Pedidos de Compra, Notas Fiscais, etc.).
 */

export interface Company {
  company_id: number;
  document: string;   // CNPJ — 14 dígitos numéricos
  name: string;
  addr_street?: string | null;
  addr_num?: string | null;
  addr_compl?: string | null;
  addr_district?: string | null;
  addr_city?: string | null;
  addr_state?: string | null;
  addr_zip?: string | null;
  addr_country?: string | null;
  state_registration_tax_id?: string | null;
  cnae_code1?: string | null;
  cnae_code2?: string | null;
  phone?: string | null;
  email?: string | null;
  /** CRT da NF-e (1 = Simples Nacional, 2 = Simples excesso, 3 = Regime Normal). */
  tax_regime_crt?: number | null;
  /** Código IBGE do município do emitente (cMun), 7 dígitos. */
  city_ibge_code?: string | null;
  /**
   * Indica se a empresa possui senha do certificado digital A1 cadastrada.
   * A senha em si nunca é retornada pelo backend.
   */
  has_certificate_password?: boolean;

  /**
   * Certificado digital A1 (e-CNPJ) — assina NF-e e e-Social.
   *
   * O arquivo vive num container privado do Blob Storage e é enviado pela tela
   * da empresa. O caminho do blob e a senha nunca saem do backend: o que chega
   * aqui é se existe certificado, de quem ele é e até quando vale.
   */
  has_certificate?: boolean;
  /** "YYYY-MM-DD" — data de calendário, exibida como veio. */
  certificate_expires_at?: string | null;
  /** CN do certificado, no formato "&lt;RAZÃO SOCIAL&gt;:&lt;CNPJ&gt;". */
  certificate_subject_cn?: string | null;
  certificate_uploaded_at?: string | null;
  certificate_uploaded_by?: string | null;
}

export interface CompanyFormData {
  document: string;
  name: string;
  addr_street?: string | null;
  addr_num?: string | null;
  addr_compl?: string | null;
  addr_district?: string | null;
  addr_city?: string | null;
  addr_state?: string | null;
  addr_zip?: string | null;
  addr_country?: string | null;
  state_registration_tax_id?: string | null;
  cnae_code1?: string | null;
  cnae_code2?: string | null;
  phone?: string | null;
  email?: string | null;
  /** CRT da NF-e (1 = Simples Nacional, 2 = Simples excesso, 3 = Regime Normal). */
  tax_regime_crt?: number | null;
  /** Código IBGE do município do emitente (cMun), 7 dígitos. */
  city_ibge_code?: string | null;
  // A senha do certificado A1 NÃO faz parte deste formulário: ela só é aceita
  // junto do arquivo, em POST /companies/:id/certificate, onde o backend confere
  // uma contra a outra antes de gravar. Gravá-la sozinha deixaria uma senha que
  // não abre o certificado em uso, e o erro só apareceria na próxima emissão.
}

/** Controle de numeração de NF-e por (modelo, série) da empresa. */
export interface InvoiceNumberControl {
  model: number;
  serie: number;
  last_number: number;
  updated_at?: string | null;
}

export interface InvoiceNumberControlPayload {
  model: number;
  serie: number;
  last_number: number;
}

export interface CompanyListFilters extends SearchModel {
  searchDocument?: string;
}

export interface CompanyListResult {
  data: Company[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Formata CNPJ de 14 dígitos para o formato XX.XXX.XXX/XXXX-XX.
 */
export const formatCnpj = (cnpj: string): string => {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5',
  );
};

export const onlyDigits = (value?: string | null): string => (value ?? '').replace(/\D/g, '');

export const formatCep = (cep?: string | null): string => {
  const digits = onlyDigits(cep).slice(0, 8);
  if (digits.length !== 8) return cep ?? '';
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const formatPhone = (phone?: string | null): string => {
  const digits = onlyDigits(phone).slice(0, 11);
  if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  return phone ?? '';
};
